import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../context/AuthContext';
import { ShieldAlert, RefreshCw, Download, Info } from 'lucide-react';

// Panel Segregation of Duties untuk halaman SAP Role Maintenance.
// Sumber data: GET /api/sap/roles/sod (backend sapSod.js) - katalog cache, tidak baca LIVE ke SAP.
export default function SodPanel({ connId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [dialogOnly, setDialogOnly] = useState(true);
  const [activeOnly, setActiveOnly] = useState(true);
  const [ruleFilter, setRuleFilter] = useState('');
  const [coFilter, setCoFilter] = useState('');
  const [q, setQ] = useState('');

  const load = async () => {
    if (!connId) return;
    setLoading(true); setErr(null);
    try {
      const res = await api.get('/api/sap/roles/sod', { params: { connectionId: connId, dialogOnly: dialogOnly ? 1 : 0, activeOnly: activeOnly ? 1 : 0 } });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Failed to load SoD analysis.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [connId, dialogOnly, activeOnly]);

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toUpperCase();
    return data.detail.filter(d =>
      (!ruleFilter || d.code === ruleFilter) &&
      (!coFilter || (d.company || '(none)') === coFilter) &&
      (!term || d.username.includes(term) || (d.department || '').toUpperCase().includes(term)));
  }, [data, ruleFilter, coFilter, q]);

  const exportCsv = () => {
    if (!rows.length) return;
    const head = ['User', 'Company', 'Department', 'User type', 'License', 'Code', 'Conflict', 'Cycle', 'Side A', 'Side B'];
    const esc = v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
    const body = rows.map(d => [d.username, d.company, d.department, d.userType, d.licenseText || d.license, d.code, d.rule, d.cycle, d.matchedA.join(' '), d.matchedB.join(' ')].map(esc).join(','));
    const blob = new Blob(['\ufeff' + [head.map(esc).join(','), ...body].join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'sod-conflicts-' + connId + '.csv';
    a.click(); URL.revokeObjectURL(a.href);
  };

  const codes = data ? data.rules.map(r => r.code) : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={dialogOnly} onChange={e => setDialogOnly(e.target.checked)} /> Dialog users only
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} /> Active only
        </label>
        <button onClick={load} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
        <button onClick={exportCsv} disabled={!rows.length} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1.5">
          <Download size={14} /> Export CSV
        </button>
        <span className="ml-auto text-xs text-slate-400">{rows.length} of {data ? data.detail.length : 0} rows</span>
      </div>

      {err && <div className="p-3 rounded-xl bg-rose-50 text-rose-700 text-sm">{err}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-white border border-slate-200"><div className="text-2xl font-semibold">{data.totals.population}</div><div className="text-xs text-slate-500">Users in scope</div></div>
            <div className="p-3 rounded-xl bg-white border border-slate-200"><div className="text-2xl font-semibold text-rose-600">{data.totals.users}</div><div className="text-xs text-slate-500">With at least one conflict</div></div>
            <div className="p-3 rounded-xl bg-white border border-slate-200"><div className="text-2xl font-semibold">{data.totals.conflicts}</div><div className="text-xs text-slate-500">Conflict rows</div></div>
            <div className="p-3 rounded-xl bg-white border border-slate-200"><div className="text-2xl font-semibold">{data.rules.length}</div><div className="text-xs text-slate-500">Active rules</div></div>
          </div>

          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex gap-2">
            <Info size={15} className="shrink-0 mt-0.5" />
            <span>Conflicts are derived from transaction codes inside each role, not from role names. Authorization field values (company code, plant) are not evaluated yet, so a pair may not actually overlap in scope. Treat this as a list to review, not to revoke. User type source: <b>{data.typeSource}</b>.</span>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            {data.rules.map(r => (
              <button key={r.code} onClick={() => setRuleFilter(ruleFilter === r.code ? '' : r.code)}
                className={`text-left p-3 rounded-xl border ${ruleFilter === r.code ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-semibold text-sm">{r.code} · {r.name}</div>
                  <div className={`text-lg font-semibold ${r.risk === 'high' ? 'text-rose-600' : 'text-amber-600'}`}>{r.users}</div>
                </div>
                <div className="text-xs text-slate-500 mt-1">{r.why}</div>
                <div className="text-[11px] text-slate-400 mt-1.5 font-mono">{r.sideA.join(' ')} &nbsp;vs&nbsp; {r.sideB.join(' ')}</div>
              </button>
            ))}
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Company</th>
                  {codes.map(c => <th key={c} className="px-3 py-2 font-medium">{c}</th>)}
                  <th className="px-3 py-2 font-medium">Users</th>
                </tr>
              </thead>
              <tbody>
                {data.byCompany.map(c => (
                  <tr key={c.company} onClick={() => setCoFilter(coFilter === c.company ? '' : c.company)}
                    className={`border-t border-slate-100 cursor-pointer ${coFilter === c.company ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                    <td className="px-3 py-2">{c.company}</td>
                    {codes.map(k => <td key={k} className="px-3 py-2 text-center tabular-nums">{c.counts[k] || 0}</td>)}
                    <td className="px-3 py-2 text-center font-semibold tabular-nums">{c.users}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search user or department..."
            className="w-full md:w-72 px-3 py-2 rounded-xl border border-slate-200 text-sm" />

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">User</th>
                  <th className="text-left px-3 py-2 font-medium">Company</th>
                  <th className="text-left px-3 py-2 font-medium">Department</th>
                  <th className="text-left px-3 py-2 font-medium">Conflict</th>
                  <th className="text-left px-3 py-2 font-medium">Side A</th>
                  <th className="text-left px-3 py-2 font-medium">Side B</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d, i) => (
                  <tr key={d.username + d.code + i} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono font-medium">{d.username}</td>
                    <td className="px-3 py-2">{d.company}</td>
                    <td className="px-3 py-2 text-slate-500">{d.department}</td>
                    <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${d.risk === 'high' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{d.code}</span> <span className="text-slate-600">{d.rule}</span></td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{d.matchedA.join(' ')}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{d.matchedB.join(' ')}</td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No conflicts for the current filter.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
